/**
 * Strings Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  DataSourceConfig,
  ExpandableDataTypeConfig
} from '../types';
import { StringsItem } from "@/lib/stores/stringsStore";
import { formatDate } from '../shared/formatters';
import { getSourceTextLabel } from '../shared/languageMappings';

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  source_text: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  section: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  context: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  abbrev: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Review', value: 'review' },
      { label: 'Approved', value: 'approved' },
      { label: 'Published', value: 'published' }
    ]
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  created_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  }
};

// Column definitions (will be dynamically adjusted based on default language)
function getColumns(defaultLanguage?: string): ColumnDefinition[] {
  return [
    { 
      key: 'source_text', 
      label: getSourceTextLabel(defaultLanguage), 
      filterable: true, 
      editable: 'text',
      metadata: fields.source_text 
    },
    { key: 'section', label: 'Section', filterable: true, metadata: fields.section },
    { key: 'context', label: 'Context', filterable: true, editable: 'text', metadata: fields.context },
    { key: 'abbrev', label: 'LocaKey', align: 'left', editable: 'text', metadata: fields.abbrev },
    { key: 'status', label: 'Status', align: 'left', editable: 'dropdown', metadata: fields.status },
    { key: 'created_at', label: 'Created', align: 'left', metadata: fields.created_at },
    { key: 'updated_at', label: 'Updated', align: 'left', metadata: fields.updated_at },
    { key: 'updated_by', label: 'Updated By', align: 'left', metadata: fields.updated_by },
    { key: 'created_by', label: 'Created By', align: 'left', metadata: fields.created_by }
  ];
}

// Configuration
const config: DataSourceConfig = {
  type: 'strings',
  title: 'Strings Original',
  description: 'Original strings with translations from the current game',
  expandable: true,
};

// Expandable configuration
const expandableConfig: ExpandableDataTypeConfig = {
  dataPath: "translations",
  displayFields: [
    { key: "language_code", label: "Language", type: "text", editable: "text" },
    { key: "translated_text", label: "Translation", type: "text", editable: "text" },
    { key: "approved", label: "Approved", type: "boolean", editable: "dropdown" }
  ]
};

// Data fetcher (strings are fetched from provider, no local fetch needed)
function fetchData(selectedGame?: any, selectedFolderId?: string | null, stringsItems?: StringsItem[]): StringsItem[] {
  return stringsItems || [];
}

// Transformers
function toList(rawData: StringsItem[]): ListWidgetItem[] {
  return rawData.map(item => ({
    id: item.id,
    name: item.source_text || 'No text',
    tags: item.section || 'String',
    type: item.status || 'String',
    created_at: item.created_at,
    data: { 
      context: item.context, 
      abbrev: item.abbrev, 
      status: item.status,
      translations: item.translations || []
    },
    updated_at: item.updated_at,
    description: item.context || 'No description'
  }));
}

function toTable(rawData: StringsItem[], defaultLanguage?: string): TableDataOutput {
  return {
    columns: getColumns(defaultLanguage),
    data: rawData.map(item => ({
      id: item.id,
      source_text: String(item.source_text || 'No text'),
      section: String(item.section || 'No section'),
      context: String(item.context || 'No context'),
      abbrev: String(item.abbrev || 'No abbreviation'),
      status: String(item.status || 'No status'),
      created_at: formatDate(item.created_at),
      updated_at: formatDate(item.updated_at),
      updated_by: String(item.updated_by || 'Unknown'),
      created_by: String(item.created_by || 'Unknown'),
      translations: item.translations || [],
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

// Export the module
export const stringsDataSource: DataSourceModule = {
  type: 'strings',
  config,
  fields,
  columns: getColumns(), // Default columns
  expandableConfig,
  fetchData,
  transformers: {
    toList,
    toTable
  }
};

